import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { signDownload } from "@/lib/signedUrl";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AuditHistoryChart from "@/components/AuditHistoryChart";
import CustomerScorecardCard from "@/components/CustomerScorecardCard";
import SafetyKPICard from "@/components/SafetyKPICard";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Activity,
  Camera,
  CheckCircle2,
  Clock,
  Download,
  FileSpreadsheet,
  FileText,
  ShieldCheck,
} from "lucide-react";

function MetricCard({ label, value, suffix = "", icon: Icon, accent = "blue", testid, hint }) {
  const accentMap = {
    blue: "text-blue-400 bg-blue-950/40 border-blue-900",
    emerald: "text-emerald-400 bg-emerald-950/40 border-emerald-900",
    amber: "text-amber-400 bg-amber-950/40 border-amber-900",
    red: "text-red-400 bg-red-950/40 border-red-900",
  };
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid={testid} title={hint || undefined}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">{label}</div>
            <div className="font-display font-black text-3xl tracking-tight mt-2">
              {value}
              {suffix && <span className="text-base font-bold text-slate-400 ml-1">{suffix}</span>}
            </div>
            {hint && (
              <div className="mt-2 text-[11px] leading-snug text-slate-500" data-testid={testid ? `${testid}-hint` : undefined}>
                {hint}
              </div>
            )}
          </div>
          <div className={`p-2.5 rounded-md border ${accentMap[accent]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownTable({ title, rows, testid }) {
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid={testid}>
      <CardContent className="p-5">
        <div className="font-display font-bold mb-3">{title}</div>
        {!rows?.length ? (
          <div className="text-sm text-slate-400">No data.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 36)}>
            <BarChart data={rows} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" stroke="#94A3B8" fontSize={11} />
              <YAxis dataKey="name" type="category" stroke="#94A3B8" fontSize={11} width={120} />
              <Tooltip
                contentStyle={{ background: "#0F172A", border: "1px solid #334155", color: "#F1F5F9", fontSize: 12 }}
                cursor={{ fill: "rgba(30,41,59,0.4)" }}
                formatter={(value, _name, item) => {
                  const r = item.payload;
                  return [`${value} projects (${r.closed} closed · ${r.photo_avg}% photo avg)`, "Total"];
                }}
              />
              <Bar dataKey="count" radius={[2, 2, 2, 2]}>
                {rows.map((row, idx) => (
                  // `row.bucket` is the stable category label (status, type,
                  // or customer). Falling back to `idx` only if a bucket
                  // label is somehow missing — Recharts requires 1:1 Cell
                  // alignment with Bar segments either way.
                  <Cell key={row.bucket ?? `bar-${idx}`} fill={idx % 2 === 0 ? "#1E3A8A" : "#3B82F6"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setData(r.data));
  }, []);

  const startExport = async (format) => {
    // Mint a fresh 5-minute signed URL on click. We can't pre-compute
    // these because the user might leave the tab open for an hour
    // before clicking — the cached URL would 401 by then.
    try {
      const kind = format === "xlsx" ? "report_xlsx" : "report_pdf";
      const url = await signDownload(kind, "all");
      window.location.href = url;
    } catch (_e) {
      // Fall back to a plain same-origin URL — the browser will attach
      // the httpOnly `f2_token` cookie automatically.
      window.location.href = `${API_BASE}/reports/export.${format}`;
    }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-400 font-display">Analytics</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="reports-heading">Reports</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => startExport("xlsx")} variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800" data-testid="report-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1.5 text-emerald-400"/> EXPORT XLSX
          </Button>
          <Button onClick={() => startExport("pdf")} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="report-export-pdf">
            <FileText className="w-4 h-4 mr-1.5"/> EXPORT PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <MetricCard label="Active" value={data?.totals?.active ?? "—"} icon={Activity} accent="blue" testid="metric-active" />
        <MetricCard label="Drafts" value={data?.totals?.drafts ?? "—"} icon={Clock} accent="amber" testid="metric-drafts" />
        <MetricCard label="Closed" value={data?.totals?.closed ?? "—"} icon={CheckCircle2} accent="emerald" testid="metric-closed" />
        <MetricCard label="Total" value={data?.totals?.total ?? "—"} icon={Activity} accent="blue" testid="metric-total" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
        <MetricCard
          label="Photo Compliance"
          value={data?.photo_compliance_avg ?? "—"}
          suffix="%"
          icon={Camera}
          accent={(data?.photo_compliance_avg ?? 0) >= 75 ? "emerald" : "amber"}
          testid="metric-photo"
        />
        <MetricCard
          label="Safety Compliance"
          value={data?.safety_compliance_pct ?? "—"}
          suffix="%"
          icon={ShieldCheck}
          accent={(data?.safety_compliance_pct ?? 0) >= 75 ? "emerald" : "amber"}
          testid="metric-safety"
          hint="Only projects with photos uploaded or a JSA are counted. Drafts excluded."
        />
        <MetricCard
          label="Avg Turnaround"
          value={data?.avg_turnaround_days ?? "—"}
          suffix={data?.avg_turnaround_days != null ? "days" : ""}
          icon={Clock}
          accent="blue"
          testid="metric-turnaround"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownTable title="Projects by Customer" rows={data?.by_customer || []} testid="breakdown-customer" />
        <BreakdownTable title="Projects by Foreman" rows={data?.by_foreman || []} testid="breakdown-foreman" />
        <BreakdownTable title="Projects by Type" rows={(data?.by_project_type || []).map(r => ({ ...r, name: r.name.replace(/_/g, " ") }))} testid="breakdown-type" />
        <BreakdownTable title="Projects by Month" rows={data?.by_month || []} testid="breakdown-month" />
      </div>

      {/* Audit-history trend chart. Sits below the breakdown tables so
          users hit the KPIs and per-dimension bars first, then get the
          time-series context underneath. */}
      <div className="mt-4">
        <AuditHistoryChart />
      </div>

      {/* Safety KPI dashboard — TRIR / DART + monthly incident volume
          + OSHA 300 CSV export. Industry-standard safety scorecard
          the customer's safety lead wants to see. */}
      <div className="mt-4">
        <SafetyKPICard />
      </div>

      {/* Customer Scorecard — deliverable for the customer's safety
          lead. Sits at the bottom because it's an occasional
          "generate + hand off" action, not a daily-viewed KPI. */}
      <CustomerScorecardCard />
    </AppShell>
  );
}
